import React, { useState } from "react";
import { Button, Card, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import CurrencyFormat from "react-currency-format";
import SlideoutMenu from "@components/SlideOutMenuV2/SlideOutMenu";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import FormNumberField from "@components/Forms/FormNumberField";
import FormDateField from "@components/Forms/FormDateField";
import ControlledDropDown from "@components/Forms/ControlledDropDown";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { MsPricingRow } from "../types/MsPricingRow";
import useStyles from "../styles/useStyles";
import EditableDataTable from "@components/EditableDataTable";
import { Column } from "@components/EditableDataTable/types/column";

interface Props {
  rows: MsPricingRow[];
  currencyUnit: string;
  isLoading: boolean;
  insightPupmEnabled: boolean;
  pricingPrivilege: boolean;
  terms: string;
  setTerms: (v: string) => void;
  startDate: string;
  setStartDate: (v: string) => void;
  billingFrequency: string;
  setBillingFrequency: (v: string) => void;
  includePsRevenue: string;
  setIncludePsRevenue: (v: string) => void;
  cola: string;
  setCola: (v: string) => void;
  projectId: string;
  onSubmitTerm: () => void;
  termError: string[] | null;
  termSuccess: string | null;
  adjustmentError: string[] | null;
  adjustmentSuccess: string | null;
  setTermSuccess: (v: string | null) => void;
  setAdjustmentSuccess: (v: string | null) => void;
  setTermError: (v: string[] | null) => void;
  setAdjustmentError: (v: string[] | null) => void;
}

const Show: React.FC<Props> = ({
  rows,
  currencyUnit,
  isLoading,
  insightPupmEnabled,
  pricingPrivilege,
  terms,
  setTerms,
  startDate,
  setStartDate,
  billingFrequency,
  setBillingFrequency,
  includePsRevenue,
  setIncludePsRevenue,
  cola,
  setCola,
  projectId,
  onSubmitTerm,
  termError,
  termSuccess,
  adjustmentError,
  adjustmentSuccess,
  setTermSuccess,
  setAdjustmentSuccess,
  setTermError,
  setAdjustmentError,
}) => {
  const styles = useStyles();
  const [showTerms, setShowTerms] = useState(false);

  const isSummary = (row: MsPricingRow) =>
    row.rowType === "totalMrr" ||
    row.rowType === "tcv" ||
    row.rowType === "profit" ||
    row.rowType === "margin";

  const formatCurrency = (value: number) => (
    <CurrencyFormat
      displayType="text"
      prefix={currencyUnit}
      isNumericString={true}
      thousandSeparator={true}
      value={value.toFixed(2)}
    />
  );

  const columns: Column[] = [
    {
      id: "service",
      header: () => "Service",
      cell(props) {
        const row = props;
        if (row.rowType === "adjustment") {
          return (
            <span>
              Price Adjustments (See{" "}
              <Link
                to={`/projects/${projectId}/project_pricing`}
                style={{ color: "#418172", fontWeight: 700 }}
              >
                Overall Pricing
              </Link>
              )
            </span>
          );
        }
        if (row.rowType === "totalMrr") return <></>;
        if (row.rowType === "tcv") return <></>;
        if (row.rowType === "profit") return <></>;
        if (row.rowType === "margin") return <></>;
        return (
          <span
            style={{
              paddingLeft: row.rowType === "subservice" ? 20 : 0,
            }}
          >
            {row.rowType === "subservice" ? `- ${row.service}` : row.service}
          </span>
        );
      },
    },
    {
      id: "quantity",
      cellClass: "quantityCol",
      header: () => "Quantity",
      cell(props) {
        const row = props;
        if (row.rowType === "adjustment") return <span>-</span>;
        if (isSummary(row)) {
          if (row.rowType === "totalMrr")
            return <span style={{ fontWeight: 700 }}>TOTAL MRR</span>;
          if (row.rowType === "tcv")
            return (
              <span style={{ fontWeight: 700 }}>TOTAL CONTRACT VALUE</span>
            );
          if (row.rowType === "profit")
            return (
              <span style={{ fontWeight: 700, color: "#418172" }}>PROFIT</span>
            );
          if (row.rowType === "margin")
            return (
              <span style={{ fontWeight: 700, color: "#418172" }}>MARGIN</span>
            );
        }
        return <span>{row.quantity}</span>;
      },
    },
    {
      id: "monthly revenue",
      header: () => "Monthly Revenue",
      cellClass: "monthlyRevenueCol",
      cell(props) {
        const row = props;
        if (row.rowType === "adjustment") {
          return <span>{formatCurrency(row.monthlyRevenue)}</span>;
        }
        if (row.rowType === "profit") {
          return (
            <span style={{ fontWeight: 700, color: "#418172" }}>
              {formatCurrency(row.monthlyRevenue)}
            </span>
          );
        }
        if (row.rowType === "margin") {
          return (
            <span style={{ fontWeight: 700, color: "#418172" }}>
              {row.monthlyRevenue
                ? `${row.monthlyRevenue.toFixed(2)}%`
                : "0.00%"}
            </span>
          );
        }
        if (row.rowType === "totalMrr" || row.rowType === "tcv") {
          return (
            <span style={{ fontWeight: 700 }}>
              {formatCurrency(row.monthlyRevenue)}
            </span>
          );
        }
        return <span>{formatCurrency(row.monthlyRevenue)}</span>;
      },
    },
    {
      id: "monthly cost",
      header: () => "Monthly Cost",
      cellClass: "monthlyCostCol",
      cell(props) {
        const row = props;
        if (row.rowType === "adjustment") return <></>;
        if (row.rowType === "profit" || row.rowType === "margin") return <></>;
        if (row.rowType === "totalMrr" || row.rowType === "tcv") {
          return (
            <span style={{ fontWeight: 700, color: "#b90200" }}>
              {formatCurrency(row.monthlyCost)}
            </span>
          );
        }
        return <span>{formatCurrency(row.monthlyCost)}</span>;
      },
    },
  ];

  return (
    <div className={styles.page}>
      {handleAlert(
        !!termError,
        termError || [],
        () => setTermError(null),
        "warning",
        ToastAlert
      )}
      {handleAlert(
        !!termSuccess,
        termSuccess || "",
        () => setTermSuccess(null),
        "success",
        ToastAlert
      )}
      {handleAlert(
        !!adjustmentError,
        adjustmentError || [],
        () => setAdjustmentError(null),
        "warning",
        ToastAlert
      )}
      {handleAlert(
        !!adjustmentSuccess,
        adjustmentSuccess || "",
        () => setAdjustmentSuccess(null),
        "success",
        ToastAlert
      )}
      <SlideoutMenu
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
        onSave={() => {
          onSubmitTerm();
          setShowTerms(false);
        }}
        title="Managed Services Term"
        actionBtnText="Save"
      >
        <Row>
          <Col sm={6}>
            <FormNumberField
              label="Length of contract (months)"
              step="1"
              min="0"
              value={terms}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTerms(e.target.value)
              }
              placeholder={undefined}
              max={undefined}
              required={false}
              defaultValue={undefined}
              readOnly={false}
              fieldFooter={undefined}
            />
          </Col>
          <Col sm={6}>
            <ControlledDropDown
              label="Billing Frequency"
              value={billingFrequency}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setBillingFrequency(e.target.value)
              }
              options={[
                <option key="monthly">Monthly</option>,
                <option key="quarterly">Quarterly</option>,
                <option key="yearly">Yearly</option>,
              ]}
              defaultValue={undefined}
              required={false}
              readOnly={false}
              disabled={false}
              fieldFooter={undefined}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={6}>
            <FormDateField
              label="Start Date"
              value={startDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setStartDate(e.target.value)
              }
              placeholder={undefined}
              defaultValue={undefined}
              required={false}
              updateRef={undefined}
            />
          </Col>
          <Col sm={6}>
            <ControlledDropDown
              label="Include PS Revenue in Recurring Payments?"
              value={includePsRevenue}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setIncludePsRevenue(e.target.value)
              }
              options={[
                <option key="do-not-include">Do Not Include PS Revenue</option>,
                <option key="add-first-month">Add To First Month</option>,
                <option key="amortize">Amortize Over All Months</option>,
              ]}
              defaultValue={undefined}
              required={false}
              readOnly={false}
              disabled={false}
              fieldFooter={undefined}
            />
            <div className="text-center">
              <a
                href={`/projects/${projectId}/project_services/payment_methods`}
                style={{ color: "#a8a8a8", fontSize: "0.9em" }}
              >
                &mdash;Or Select Individual Services&mdash;
              </a>
            </div>
          </Col>
        </Row>
        {insightPupmEnabled && (
          <Row>
            <Col sm={6}>
              <FormNumberField
                label="Cost of Living Adjustment (%) *"
                required={true}
                step="0.01"
                min="0"
                value={cola}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCola(e.target.value)
                }
                placeholder={undefined}
                max={undefined}
                defaultValue={undefined}
                readOnly={false}
                fieldFooter={undefined}
              />
            </Col>
          </Row>
        )}
      </SlideoutMenu>

      <div className={styles.rightAlignColumns}>
        <Card className={styles.card}>
          <Card.Header className={styles.cardHeader}>
            <Row className={styles.headerRow}>
              <Col>
                <div className={styles.title}>Managed Services Summary</div>
                <div className={styles.subtitle}>
                  <span style={{ fontWeight: 700 }}>Payment Terms: </span>
                  {[
                    terms ? `${terms} ${billingFrequency} payments` : null,
                    startDate
                      ? `starting ${new Date(
                          startDate + "T00:00:00"
                        ).toLocaleDateString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                        })}`
                      : null,
                    includePsRevenue || null,
                  ]
                    .filter(Boolean)
                    .join(", ") || "—"}
                </div>
              </Col>
              <Col
                className="text-end"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Button
                  className="btnSeawhite"
                  onClick={() => setShowTerms(true)}
                >
                  Payment Terms
                </Button>
              </Col>
            </Row>
          </Card.Header>
          <hr className={styles.hr} />
          <Card.Body>
            {isLoading ? (
              <ScopeStackSpinner />
            ) : (
              <EditableDataTable
                records={rows}
                columns={columns}
                config={{ bordered: true, striped: true, hover: true }}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default Show;
